/**
 * Vite's `vite:preloadError`: a lazily imported chunk could not be loaded, typically because a deploy
 * replaced it while this tab was open (ADR 0019). Reload once to get the new version; if that already
 * happened in the last minute, do nothing more (the error boundary shows a reload button instead of a
 * reload loop).
 */
export const PRELOAD_RELOAD_KEY = "fts.preloadReloadAt";
export const PRELOAD_RELOAD_WINDOW_MS = 60_000;

type Store = { getItem(k: string): string | null; setItem(k: string, v: string): void };

/** true = reload now (and remember it); false = we just reloaded for this, let the boundary handle it. */
export function shouldReloadForPreloadError(store: Store | null, now: number): boolean {
  // Without storage the reload cannot be remembered across page loads: never reload automatically
  // (otherwise every load would reload again). The error boundary offers a manual reload.
  if (!store) return false;
  try {
    const last = Number(store.getItem(PRELOAD_RELOAD_KEY) ?? 0);
    if (last && now - last < PRELOAD_RELOAD_WINDOW_MS) return false;
    store.setItem(PRELOAD_RELOAD_KEY, String(now));
    // Read back: a storage that silently drops writes would also loop.
    if (store.getItem(PRELOAD_RELOAD_KEY) !== String(now)) return false;
    return true;
  } catch {
    // No storage: cannot guard against a loop, so do not reload automatically.
    return false;
  }
}

export function installPreloadRecovery(win: Window = window, reload: () => void = () => win.location.reload()): () => void {
  const onError = (e: Event) => {
    let store: Store | null = null;
    try {
      store = win.sessionStorage;
    } catch {
      store = null;
    }
    if (!shouldReloadForPreloadError(store, Date.now())) return;
    // Handled: the import promise does not reject, the page reloads.
    e.preventDefault();
    reload();
  };
  win.addEventListener("vite:preloadError", onError);
  return () => win.removeEventListener("vite:preloadError", onError);
}
